'use client';

import { useOverlayTriggerState } from '@react-stately/overlays';
import { AnimatePresence, motion } from 'framer-motion';
import type { ComponentType} from 'react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';

import type { IconProps } from '@zoomit/icons';
import { Alert, AvatarScratch } from '@zoomit/icons';
import { cn, IconButton } from '@zoomit/primitives';
import {
  DesktopNotification,
  DesktopUserMenu,
  GlobalLoading,
  LogoutModal,
  NotifBadge,
  onNotificationClick,
  useLogout,
} from '@zoomit/web-kit';
import { cssVar, theme } from '@zoomit/zoomit-ds';

type Props = {
  Logo: ComponentType<IconProps>;
};

export const UserSection = ({ Logo }: Props) => {
  const { isAuthenticated, signinRedirect, activeNavigator, isLoading } = useAuth();
  const { isLoading: signOutLoading, mutate: handleSignOut } = useLogout();

  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const notificationDrawer = useOverlayTriggerState({});

  return (
    <>
      {activeNavigator === 'signinRedirect' && <GlobalLoading Logo={Logo} />}
      <AnimatePresence>
        {isAuthenticated && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            whileTap={{ scale: 0.9 }}
            transition={{ delay: 0.1 }}
          >
            <IconButton
              onClick={() => {
                notificationDrawer.toggle();
                onNotificationClick();
              }}
              style={{ position: 'relative' }}
            >
              <NotifBadge />
              <Alert
                className={cn(
                  'fill-text cursor-pointer transition duration-200',
                  notificationDrawer.isOpen && 'fill-primary1',
                )}
              />
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>
      {isAuthenticated ? (
        <DesktopUserMenu setShowLogoutModal={setShowLogoutModal} />
      ) : (
        <IconButton onClick={() => signinRedirect({ url_state: window.location.href })}>
          <AvatarScratch size="lg" fill={cssVar(theme.text)} />
        </IconButton>
      )}
      {(isAuthenticated || isLoading) && showLogoutModal && (
        <LogoutModal
          mutate={handleSignOut}
          isLoading={signOutLoading}
          onClose={() => setShowLogoutModal(false)}
        />
      )}
      {isAuthenticated && notificationDrawer.isOpen && (
        <AnimatePresence>
          <DesktopNotification menuHeight={133} onClose={notificationDrawer.close} />
        </AnimatePresence>
      )}
    </>
  );
};
